"use client"

import { useState, useEffect, useRef } from "react"
import { Check, X, Plus, Trash2 } from "lucide-react"
import { createTag, deleteTag } from "@/lib/supabase/queries"
import { cn } from "@/lib/utils"

interface BlogTagRow {
  id: string
  name: string
  slug: string
  createdAt: Date
}

interface TagMultiSelectProps {
  tags: BlogTagRow[]
  selected: string[]
  onChange: (selected: string[]) => void
}

export function TagMultiSelect({ tags, selected, onChange }: TagMultiSelectProps) {
  const [isCreating, setIsCreating] = useState(false)
  const [newTagName, setNewTagName] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [allTags, setAllTags] = useState(tags)
  const [deleteTarget, setDeleteTarget] = useState<BlogTagRow | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    if (deleteTarget) {
      dialog.showModal()
    } else {
      dialog.close()
    }
  }, [deleteTarget])

  const toggleTag = (tagId: string) => {
    if (selected.includes(tagId)) {
      onChange(selected.filter((id) => id !== tagId))
    } else {
      onChange([...selected, tagId])
    }
  }

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return

    setIsSubmitting(true)
    try {
      const slug = newTagName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")

      const result = await createTag({ name: newTagName, slug })

      if (result.success && result.id) {
        const newTag: BlogTagRow = {
          id: result.id,
          name: newTagName,
          slug,
          createdAt: new Date(),
        }
        setAllTags([...allTags, newTag])
        onChange([...selected, result.id])
        setNewTagName("")
        setIsCreating(false)
      }
    } catch (error) {
      console.error("Failed to create tag:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteTag = async () => {
    if (!deleteTarget) return

    setIsDeleting(true)
    try {
      const result = await deleteTag(deleteTarget.id)
      if (result.success) {
        setAllTags(allTags.filter((t) => t.id !== deleteTarget.id))
        onChange(selected.filter((id) => id !== deleteTarget.id))
        setDeleteTarget(null)
      } else {
        alert(result.error || "Failed to delete tag")
      }
    } catch (error) {
      console.error("Failed to delete tag:", error)
      alert("Failed to delete tag")
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        {allTags.map((tag) => (
          <div
            key={tag.id}
            className={cn(
              "inline-flex items-center text-sm rounded-full transition-colors",
              selected.includes(tag.id)
                ? "bg-primary text-white"
                : "bg-bg-alt text-text-secondary hover:bg-primary/10 hover:text-primary"
            )}
          >
            <button
              type="button"
              onClick={() => toggleTag(tag.id)}
              className="inline-flex items-center gap-1 pl-3 py-1.5"
            >
              {selected.includes(tag.id) && <Check className="w-3 h-3" />}
              {tag.name}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setDeleteTarget(tag)
              }}
              className={cn(
                "p-1 mx-1 rounded-full transition-colors",
                selected.includes(tag.id)
                  ? "text-white/70 hover:text-white hover:bg-white/20"
                  : "text-text-muted hover:text-red-500 hover:bg-red-100"
              )}
              title="Delete tag"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
      </div>

      {isCreating ? (
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={newTagName}
            onChange={(e) => setNewTagName(e.target.value)}
            placeholder="Tag name"
            className="flex-1 px-3 py-1.5 text-sm border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            autoFocus
          />
          <button
            type="button"
            onClick={handleCreateTag}
            disabled={isSubmitting || !newTagName.trim()}
            className="p-1.5 text-primary hover:bg-primary/10 rounded-lg disabled:opacity-50"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              setIsCreating(false)
              setNewTagName("")
            }}
            className="p-1.5 text-text-muted hover:bg-bg-alt rounded-lg"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setIsCreating(true)}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-text-muted hover:text-text border border-dashed border-border rounded-full hover:border-text-muted transition-colors"
        >
          <Plus className="w-3 h-3" />
          Add tag
        </button>
      )}

      {/* Delete Confirm Dialog */}
      <dialog
        ref={dialogRef}
        className={cn(
          "fixed inset-0 z-50 m-auto",
          "w-full max-w-sm p-0",
          "bg-transparent backdrop:bg-black/60"
        )}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            setDeleteTarget(null)
          }
        }}
      >
        <div
          className={cn(
            "bg-bg-card border border-border rounded-xl",
            "p-6 shadow-2xl"
          )}
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-text">Delete Tag</h3>
            </div>
            <button
              onClick={() => setDeleteTarget(null)}
              className={cn(
                "p-1 rounded-lg",
                "text-text-muted hover:text-text hover:bg-bg-alt",
                "transition-colors"
              )}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-6">
            <p className="text-text-secondary">
              Are you sure you want to delete{" "}
              <span className="font-medium text-text">&ldquo;{deleteTarget?.name}&rdquo;</span>?
            </p>
            <p className="text-text-muted text-sm mt-2">
              This will remove the tag from all posts.
            </p>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
              className={cn(
                "px-4 py-2 rounded-lg",
                "bg-bg-alt hover:bg-bg",
                "text-text-secondary hover:text-text",
                "transition-colors",
                "disabled:opacity-50"
              )}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDeleteTag}
              disabled={isDeleting}
              className={cn(
                "px-4 py-2 rounded-lg",
                "bg-red-500 hover:bg-red-600",
                "text-white font-medium",
                "transition-colors",
                "disabled:opacity-50"
              )}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </dialog>
    </div>
  )
}
